import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdArrowBack } from "react-icons/md";
import { IoRefresh } from "react-icons/io5";
import server from "../../../api/server.js";
import "../../../dashboard-components/loader.css";
import { useToast } from "../../../context/ToastContext.jsx";

const PROCESSING_STATUS_LABELS = {
  pending: "Pending",
  success: "Success",
  no_action: "No Action",
  failed: "Failed",
};
const PROCESSING_STATUS_CLASSES = {
  pending: "bg-yellow-100 text-yellow-800",
  success: "bg-green-100 text-green-800",
  no_action: "bg-gray-100 text-gray-700",
  failed: "bg-red-100 text-red-800",
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : null;
};

const formatEventTime = (epochSeconds) => {
  if (!epochSeconds) return "-";
  const date = new Date(Number(epochSeconds) * 1000);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
};

const formatFieldLabel = (field) => field.split("_").map((word) => {
  if (["id", "crm"].includes(word)) return word.toUpperCase();
  return word.charAt(0).toUpperCase() + word.slice(1);
}).join(" ");

const EventDetails = ({ selectedOption, selectedClientId, eventReferenceIdProp, onClose }) => {
  const { eventReferenceId: routeEventReferenceId } = useParams();
  const eventReferenceId = eventReferenceIdProp || routeEventReferenceId;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [email] = useState(() => {
    try {
      const user = getCookie("dashboard_login_user_creds");
      return user ? JSON.parse(decodeURIComponent(user)).email : null;
    } catch (error) {
      return null;
    }
  });
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const getEventDetails = useCallback(async () => {
    if (!selectedClientId || !selectedOption || !email || !eventReferenceId) return;
    try {
      setLoading(true);
      setNotFound(false);
      const res = await server.getClientEventDetails(selectedClientId, selectedOption, email, eventReferenceId, "events", undefined);
      const resp = await res.json();
      if (res.status === 200) {
        setEvent(resp.data);
      } else {
        setEvent(null);
        setNotFound(res.status === 404);
        if (res.status !== 404) showToast(resp.error || "Unable to load event details", "error");
      }
    } catch (error) {
      showToast("Unable to load event details", "error");
    }
    setLoading(false);
  }, [selectedClientId, selectedOption, email, eventReferenceId, showToast]);

  useEffect(() => {
    getEventDetails();
  }, [getEventDetails]);

  const goToEventRecords = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(`/analytics/${selectedClientId}/${selectedOption}/events`);
  };

  const openCustomer = (customerId) => {
    navigate(`/analytics/${selectedClientId}/${selectedOption}/customers/${encodeURIComponent(customerId)}`);
  };

  const fields = event ? [
    ["Event Name", event.event_name],
    ["Event Status", event.event_status],
    ["Event Time", formatEventTime(event.event_time)],
    ["Event ID", event.event_id],
  ] : [];

  return (
    <div className="bg-gray-50 font-sans" style={{ padding: "0 1rem", minHeight: "100%" }}>
      <div className={`flex justify-between items-center ${onClose ? "pr-8" : ""}`}>
        <button onClick={goToEventRecords} style={{ display: "flex", alignItems: "center", color: "#4b5563", cursor: "pointer", border: "none", background: "none", padding: "8px", fontSize: "14px" }}>
          <MdArrowBack style={{ marginRight: "8px" }} />
          Back
        </button>
        <button onClick={getEventDetails} disabled={loading} className="flex items-center px-4 py-1 my-1" style={{ fontSize: "14px", color: loading ? "#9ca3af" : "#4b5563" }}>
          <IoRefresh />
          <span className="ml-1">{loading ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {loading && !event && (
        <div className="flex justify-center items-center" style={{ height: "60vh", flexDirection: "column" }}>
          <div className="loader"></div>
          <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
        </div>
      )}

      {!loading && notFound && (
        <div className="bg-white rounded-lg shadow-sm border p-6 text-sm text-gray-500">
          No event found with reference ID "{eventReferenceId || ""}" for this service.
        </div>
      )}

      {event && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 pb-6">
          <div className="lg:col-span-2 space-y-4 min-w-0">
            <section className="bg-white rounded-lg shadow-sm p-4">
              <h1 className="text-base font-medium text-gray-900 mb-4">Event Details</h1>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col sm:col-span-2">
                  <dt className="text-gray-500 text-xs mb-1">Event Reference ID</dt>
                  <dd className="text-gray-900 break-all">{event.event_reference_id}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-gray-500 text-xs mb-1">Customer ID</dt>
                  <dd><button type="button" className="text-blue-600 text-left break-all hover:underline" onClick={() => openCustomer(event.customer_id)}>{event.customer_id}</button></dd>
                </div>
                {fields.map(([label, value]) => (
                  <div key={label} className="flex flex-col">
                    <dt className="text-gray-500 text-xs mb-1">{label}</dt>
                    <dd className="text-gray-900 break-all">{value ?? "-"}</dd>
                  </div>
                ))}
              </dl>
            </section>

            <section className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-base font-medium text-gray-900 mb-4">Event Information</h2>
              {Object.keys(event.event_data || {}).length ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {Object.entries(event.event_data).map(([field, value]) => (
                    <div key={field}>
                      <dt className="text-gray-500 text-xs mb-1">{formatFieldLabel(field)}</dt>
                      <dd className="text-gray-900 break-words whitespace-pre-wrap">
                        {field === "crm_lead_created_at" ? formatEventTime(value) : value == null ? "-" : typeof value === "object" ? JSON.stringify(value) : String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : <p className="text-sm text-gray-500">No additional fields were sent with this event.</p>}
            </section>

            <details className="bg-white rounded-lg shadow-sm p-4">
              <summary className="text-sm font-medium text-gray-700 cursor-pointer">Raw Request</summary>
              <pre className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-700 whitespace-pre-wrap break-words">
                {event.raw_payload ? JSON.stringify(event.raw_payload, null, 2) : "Not available"}
              </pre>
            </details>
          </div>
          <section className="bg-white rounded-lg shadow-sm p-4 self-start min-w-0">
            <h2 className="text-base font-medium text-gray-900 mb-4">Processing Details</h2>
            <dl className="space-y-4 text-sm">
              <div>
                <dt className="text-gray-500 text-xs mb-2">Processing Status</dt>
                <dd><span className={`px-2 py-0.5 rounded text-xs ${PROCESSING_STATUS_CLASSES[event.processing_status] || "bg-gray-100 text-gray-700"}`}>
                  {PROCESSING_STATUS_LABELS[event.processing_status] || event.processing_status || "-"}
                </span></dd>
              </div>
              <div><dt className="text-gray-500 text-xs mb-1">Received At</dt><dd>{formatEventTime(event.received_at)}</dd></div>
              <div><dt className="text-gray-500 text-xs mb-1">Last Updated</dt><dd>{formatEventTime(event.updated_at)}</dd></div>
            </dl>
            {event.processing_error && (
              <div role="alert" className="mt-4 rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 break-words">{event.processing_error}</div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
